import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../styles/site-pages.css';

/**
 * /sitemap — human-readable site navigation (issue #263).
 *
 * Every entry below was checked against the <Route> table in App.js, so this
 * page cannot advertise a path that 404s — which would defeat the point of
 * the issue it closes.
 *
 * Two deliberate omissions:
 *  · Assess-A-Thon (/assessathon) — still routed, but its footer link was
 *    pulled on 2026-08-18 "per sir's instruction". Re-listing it here would
 *    quietly undo that decision, so it stays off.
 *  · /team — the page exists in src/pages but was unrouted on 2026-08-20 for
 *    the same reason. Linking it would 404.
 * Both are decisions someone made on purpose; this page is not the place to
 * reverse them.
 *
 * Signed-in areas are listed but not linked: a logged-out visitor (and an
 * Apple reviewer) clicking them would be bounced to /login, which reads like
 * a broken link even though it is correct behaviour.
 */
const GROUPS = [
  {
    title: 'Platform',
    note: 'The product and how it works.',
    links: [
      { to: '/', label: 'Home' },
      { to: '/learn-more', label: 'How it works' },
      { to: '/pricing', label: 'Pricing' },
      { to: '/download-mars', label: 'Download MARS' },
      { to: '/challenge-the-trust', label: 'Challenge the Trust' },
    ],
  },
  {
    title: 'Company',
    note: 'Who we are and how to reach us.',
    links: [
      { to: '/about', label: 'About Us' },
      { to: '/contact', label: 'Contact' },
      { to: '/become-a-fellow', label: 'Become a Fellow' },
      { to: '/nominations', label: 'Nominations' },
    ],
  },
  {
    title: 'Legal',
    note: 'Terms, privacy and the rest of the policy set.',
    links: [
      { to: '/privacy', label: 'Privacy Policy' },
      { to: '/terms', label: 'Terms of Service' },
      { to: '/policies/subscription-billing-refund', label: 'Billing & Refund Policy' },
      { to: '/policies/cookie-policy', label: 'Cookie Policy' },
      { to: '/policies/sla-addendum', label: 'SLA Addendum' },
      { to: '/policies/disclaimers', label: 'Disclaimers' },
    ],
  },
  {
    title: 'Account',
    note: 'Sign in, or create an account.',
    links: [
      { to: '/login', label: 'Log in' },
      { to: '/signup', label: 'Sign up' },
      { to: '/forgot-password', label: 'Reset password' },
    ],
  },
  {
    title: 'Institutions',
    note: 'Partner and campus entry points.',
    links: [
      { to: '/lpu', label: 'LPU Faculty' },
      { to: '/lpu/login', label: 'LPU sign in' },
      { to: '/freshmen2026', label: 'Freshmen 2026' },
    ],
  },
  {
    title: 'Signed-in areas',
    note: 'These need an account, so they are listed rather than linked.',
    links: [
      { label: 'Studio', path: '/studio' },
      { label: 'Lesson generator', path: '/generator' },
      { label: 'Fellowship dashboard', path: '/fellowship' },
      { label: 'Redemption store', path: '/fellowship/store' },
      { label: 'Settings', path: '/settings' },
    ],
  },
];

export default function SitemapPage() {
  return (
    <div className="site-page">
      <Helmet>
        <title>Sitemap — TeachGenie™.ai</title>
        <meta
          name="description"
          content="Every public page on TeachGenie™.ai — platform, company, legal, account and institution pages, in one list."
        />
        <link rel="canonical" href="https://www.teachgenie.ai/sitemap" />
      </Helmet>

      <Navbar />

      <main className="site-page-inner">
        <span className="site-page-kicker">Sitemap</span>
        <h1>Everything, in one list.</h1>
        <p className="site-page-lede">
          Every public page on TeachGenie™.ai. If you came here because something 404'd,{' '}
          <Link to="/contact">tell us</Link> and we will fix it.
        </p>

        <div className="sitemap-groups">
          {GROUPS.map(group => (
            <section className="sitemap-group" key={group.title}>
              <h2>{group.title}</h2>
              <p className="group-note">{group.note}</p>
              <ul>
                {group.links.map(link => (
                  <li key={link.label}>
                    {link.to ? (
                      <Link to={link.to}>
                        <span>{link.label}</span>
                        <span className="path">{link.to}</span>
                      </Link>
                    ) : (
                      <span title="Requires a signed-in account">
                        <span>{link.label}</span>
                        <span className="path">{link.path}</span>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}
